import styles from './FacetHistogram.module.css'

interface FacetHistogramProps {
  /** Bucket counts over the control's scale; bars scale to the tallest bucket. */
  distribution: number[]
  /** Bucket index range currently inside the selection (inclusive); others dim. */
  activeFrom?: number
  activeTo?: number
  compact?: boolean
}

/**
 * The bare bar row shared by range and score controls — the same bar language as
 * the product page's DistributionBars (clr(g-70) rest, clr(dh-bar-fill) selected).
 * Purely decorative: the slider/readout carries the accessible value.
 *
 * Two honesty rules, because the chart's one job is showing where products live:
 * an empty bucket renders as a flat baseline tick (never a bar that reads as "a
 * few products down here"), and heights are √-scaled against the tallest bucket
 * so a 1-product bucket is visibly present next to a 25-product one. Heights are
 * a ranked cue, not proportional — acceptable for an aria-hidden facet chart.
 */
const FacetHistogram: React.VFC<FacetHistogramProps> = ({
  distribution,
  activeFrom = 0,
  activeTo = distribution.length - 1,
  compact,
}) => {
  if (distribution.length === 0) {
    return null
  }
  const max = Math.max(1, ...distribution)
  return (
    <div className={styles.bars} data-compact={compact || undefined} aria-hidden="true">
      {distribution.map((count, i) => {
        const zero = count === 0
        return (
          <span
            // Buckets are positional; the index is the identity.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={styles.bar}
            data-zero={zero || undefined}
            data-active={!zero && i >= activeFrom && i <= activeTo}
            data-edge={!zero && i === activeFrom && activeFrom > 0}
            style={zero ? undefined : { height: `${Math.max(9, Math.sqrt(count / max) * 100)}%` }}
          />
        )
      })}
    </div>
  )
}

export default FacetHistogram
